<template>
  <teleport to="#headerPageContent">
    <HeaderBarItem
      v-if="device"
    >
      <HeaderIconButton
        @click="is360ModalActive = true"
      >
        <template #icon>
          <BoxCircleSvg />
        </template>

        <template #label>
          View device in 360º
        </template>
      </HeaderIconButton>
    </HeaderBarItem>
    <HeaderBarItem>
      <ProductsSwitchViewButton
        v-model="viewMode"
      />
    </HeaderBarItem>
  </teleport>

  <div :class="$style.page">
    <BaseContainer>
      <BaseLoader
        :is-active="!isDataLoaded"
        is-block
      >
        <template v-if="device">
          <ProductsDeviceHeader :device-data="device" />

          <div :class="$style.illustration">
            <ProductsDeviceImages
              :class="$style.images"
              :front-image="device.front_image || defaultFrontImage"
            />
            <div :class="$style.chartWrapper">
              <VolumesChart
                v-if="chartData"
                :class="$style.chart"
                :aspect-ratio="1"
                :datasets="chartData.datasets"
                :labels="chartData.labels"
              />
            </div>
          </div>
        </template>
      </BaseLoader>
    </BaseContainer>
    <div :class="$style.sections">
      <div
        v-if="mainLightFunctions.length"
        :class="$style.section"
      >
        <BaseContainer>
          <div :class="$style.sectionHeader">
            <h3 :class="$style.sectionHeading">
              LDV light configuration
            </h3>
          </div>
        </BaseContainer>
        <BaseContainer>
          <BaseTable
            :data="mainLightFunctions"
            is-block
          >
            <BaseTableColumn
              field="light_source.technology"
            >
              <template #header>
                <TableHeaderWithIcon>
                  <TableTypeSvg /> Source
                </TableHeaderWithIcon>
              </template>
            </BaseTableColumn>
            <BaseTableColumn
              header="Light Function"
              field="light_function.name"
            />
            <BaseTableColumn
              field="light_functionality.name"
            >
              <template #header>
                <TableHeaderWithIcon>
                  <TableComplexitySvg /> Light Functionality
                </TableHeaderWithIcon>
              </template>
            </BaseTableColumn>
            <BaseTableColumn
              field="optical_system.name"
            >
              <template #header>
                <TableHeaderWithIcon>
                  <TableJigsawSvg /> Optical System
                </TableHeaderWithIcon>
              </template>
            </BaseTableColumn>
          </BaseTable>
        </BaseContainer>
      </div>
      <div
        v-if="cars.length"
        :class="$style.section"
      >
        <BaseContainer>
          <div :class="$style.sectionHeader">
            <h3 :class="$style.sectionHeading">
              Vehicles that use this device
            </h3>
            <p :class="$style.sectionSubheading">
              There are {{ cars.length }} vehicles used by this vehicle.
            </p>
          </div>
        </BaseContainer>
        <BaseContainer v-if="viewMode === 'list'">
          <BaseTable
            is-block
            :data="cars"
          >
            <CarsTableColumns has-brand />
          </BaseTable>
        </BaseContainer>
        <ProductsCardsGrid
          v-else-if="viewMode === 'grid'"
        >
          <ProductsCarCard
            v-for="car in cars"
            :key="car.id"
            :car-id="car.id"
            :model="car.model"
            :brand-logo="car.brand.logo"
            :brand-name="car.brand.name"
            :project-code="car.project_code"
            :power-train="car.power_train"
            :body="car.body"
            :generation="car.generation"
            :phase="car.phase"
            :front-image="car.front_image"
            :back-image="car.back_image"
            :sop="car.volumes_infos.sop"
            :eop="car.volumes_infos.eop"
            :source-factory-name="car.source_factory.name"
            :country="car.country"
          />
        </ProductsCardsGrid>
      </div>
    </div>
  </div>
  <BaseModal v-model="is360ModalActive">
    <iframe
      v-if="device"
      :class="$style.iframe"
      :src="device.object_360"
      width="500"
      height="350"
    />
  </BaseModal>
</template>

<script>
import { mapState } from 'vuex';
import gql from 'graphql-tag';
import { format } from 'date-fns';
import { maxBy, minBy } from 'lodash';
import BaseContainer from '@/components/base/Container';
import BaseLoader from '@/components/base/Loader';
import BaseModal from '@/components/base/Modal';
import ProductsDeviceHeader from '@/components/ProductsDeviceHeader';
import ProductsDeviceImages from '@/components/ProductsDeviceImages';
import BaseTable, { TableColumn as BaseTableColumn } from '@/components/base/Table';
import VolumesChart from '@/components/VolumesChart';
import TableHeaderWithIcon from '@/components/TableHeaderWithIcon';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderIconButton from '@/components/HeaderIconButton';
import ProductsCardsGrid from '@/components/ProductsCardsGrid';
import ProductsCarCard from '@/components/ProductsCarCard';
import ProductsSwitchViewButton from '@/components/ProductsSwitchViewButton';
import CarsTableColumns from '@/components/CarsTableColumns';
import BoxCircleSvg from '@/assets/images/icons/box-circle.svg?inline';
import TableTypeSvg from '@/assets/images/icons/table-type.svg?inline';
import TableJigsawSvg from '@/assets/images/icons/table-jigsaw.svg?inline';
import TableComplexitySvg from '@/assets/images/icons/table-complexity.svg?inline';
import { convertGroupedByYearToChartData, groupByYear, styleYearSegments } from '@/charts';

export default {
  components: {
    BaseContainer,
    BaseLoader,
    BaseModal,
    ProductsDeviceHeader,
    ProductsDeviceImages,
    BaseTable,
    BaseTableColumn,
    VolumesChart,
    TableHeaderWithIcon,
    HeaderBarItem,
    HeaderIconButton,
    ProductsCardsGrid,
    ProductsCarCard,
    ProductsSwitchViewButton,
    CarsTableColumns,
    BoxCircleSvg,
    TableTypeSvg,
    TableJigsawSvg,
    TableComplexitySvg,
  },
  apollo: {
    mainLightFunctions: {
      query: gql`
        query($deviceId: Int!) {
          mainLightFunctions: main_light_functions(where: { lighting_device_id: { _eq: $deviceId } }) {
            light_function {
              name
            }
            light_functionality {
              name
            }
            light_source {
              technology
            }
            optical_system {
              name
            }
          }
        }
      `,
      variables() {
        return {
          deviceId: this.$route.params.deviceId,
        };
      },
      skip() {
        return this.$route.params.deviceId == null;
      },
    },
  },
  data() {
    return {
      device: null,
      cars: [],
      viewMode: 'grid',
      volumes: null,
      mainLightFunctions: [],
      is360ModalActive: false,
      isDataLoaded: false,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('app', ['carsId']),
    ...mapState('app', ['updateReferenceId']),
    defaultFrontImage() {
      switch (this.device.type) {
        case 'HL':
          return '/images/default-hl-front.jpg';
        case 'RL':
          return '/images/default-rl-front.jpg';
        default:
          return null;
      }
    },
    chartData() {
      if (this.volumes == null) return null;

      const groupedByYears = groupByYear(this.volumes, 'lighting_configuration.lighting_device.id');

      const yearsSet = new Set();
      this.volumes.forEach((item) => yearsSet.add(item.year));

      return {
        labels: Array.from(yearsSet).sort(),
        datasets: [
          {
            label: 'This device',
            borderColor: '#B4E43B',
            backgroundColor: '#B4E43B',
            pointBackgroundColor: '#fff',
            segment: {
              borderDash: styleYearSegments,
            },
            data: convertGroupedByYearToChartData(groupedByYears),
          },
        ],
      };
    },
  },
  async created() {
    this.cars = await this.loadCars(this.$route.params.deviceId, this.user, this.carsId);
    this.device = await this.loadDevice(this.$route.params.deviceId, this.user);
  },
  methods: {
    async loadDevice(deviceId, user) {
      const { data } = await this.$apollo.query({
        query: gql`
          query {
            device: lighting_devices_by_pk(id: ${deviceId}) {
              id
              type
              parts_per_car
              part_number
              complexity
              weight
              dim_x
              dim_y
              dim_z
              aftermarket_price
              front_image
              object_360
              factory {
                name
              }
              supplier {
                name
                region
                country
              }
              lighting_configurations {
                car {
                  volumes_infos {
                    sop: sop_${user.company.uuid.replace(/-/g, '')}
                    eop: eop_${user.company.uuid.replace(/-/g, '')}
                  }
                }
              }
            }
          }
        `,
      });

      const lightingConfigurations = data.device.lighting_configurations.map((lc) => {
        let result = lc.car;
        result = {
          ...result,
          volumes_infos: result.volumes_infos[0],
        };
        return {
          ...lc,
          car: result,
        };
      });

      const minSopConfiguration = minBy(lightingConfigurations, 'car.volumes_infos.sop');
      const maxEopConfiguration = maxBy(lightingConfigurations, 'car.volumes_infos.eop');
      this.loadVolume(data.device.id, user, this.updateReferenceId);
      this.isDataLoaded = true;

      return {
        ...data.device,
        min_sop: minSopConfiguration?.car.volumes_infos.sop,
        max_eop: maxEopConfiguration?.car.volumes_infos.eop,
      };
    },
    format,
    async loadCars(deviceId, user, selectedCars) {
      const { data } = await this.$apollo.query({
        query: gql`
          query($selectedCars: [Int!]){
            cars: lighting_configurations(
              where: {
                lighting_device_id: {_eq: ${deviceId}},
                car_id: {_in: $selectedCars},
              },
              order_by: {id: asc}
            ) {
              car {
                id
                model
                project_code
                segment
                body
                generation
                phase
                production_type
                volumes_infos {
                 sop: sop_${user.company.uuid.replace(/-/g, '')}
                 eop: eop_${user.company.uuid.replace(/-/g, '')}
                 has_volumes: has_volumes_${user.company.uuid.replace(/-/g, '')}
                }
                commercial_name
                power_train
                front_image
                back_image
                country {
                  region
                  code
                }
                brand {
                  name
                  logo
                }
                source_factory {
                  id
                  name
                }
              }
            }
          }
        `,
        variables: {
          selectedCars,
        },
      });

      const res = data.cars.map((lc) => {
        let result = lc.car;
        result = {
          ...result,
          volumes_infos: result.volumes_infos[0],
        };
        return result;
      }).filter((item) => (item.volumes_infos.has_volumes
        && new Date(item.volumes_infos.eop) >= new Date(2017, 0, 1)));

      return res;
    },
    async loadVolume(deviceId, user, updateRefId) {
      const { data } = await this.$apollo.query({
        query: gql`query($updateRefId: String!){
            volumes: third_party_take_rates_aggregated(
              where: {
                ldv_id: {_eq: ${deviceId}}, 
                _and: {
                  volumes_${user.company.uuid.replace(/-/g, '')}: {_is_null: false, _neq: 0}
                  update_ref_id_${user.company.uuid.replace(/-/g, '')}: {_eq: $updateRefId}
                  }
              }
            ) {
              sources_units: volumes_${user.company.uuid.replace(/-/g, '')}
              rate
              parts_per_car
              year
            }
          }
        `,
        variables: {
          updateRefId,
        },
      });

      this.volumes = data.volumes.map((item) => ({
        ...item,
        // eslint-disable-next-line
        units: Math.round((item.sources_units ? item.sources_units : null) * (item.rate ? item.rate : 0) * (item.parts_per_car ? item.parts_per_car : 0)),
      }));
    },
  },
};
</script>

<style lang="scss" module>
.page {
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
}

.illustration {
  display: flex;
  padding: 0 15px;
  margin-top: 20px;
}

.images {
  flex-grow: 1;
}

.chartWrapper {
  padding: 15px 20px;
  margin-left: 8px;
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1),
    0 3px 20px rgba(0, 0, 0, 0.1);
}

.chart {
  width: 350px;
}

.sections {
  margin-top: 15px;
}

.section {
  margin-top: 35px;

  &:not(:first-child) {

    .sectionHeader {
      position: relative;
      padding-top: 35px;

      &::before {
        position: absolute;
        top: 0;
        right: 15px;
        left: 15px;
        display: block;
        width: 100%;
        height: 1px;
        content: '';
        background-color: rgba($disabled, 0.4);
      }
    }
  }
}

.sectionHeader {
  padding: 0 15px;
}

.sectionHeading {
  padding: 0;
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  color: $title;
}

.sectionSubheading {
  margin: 0;
  margin-top: 4px;
  font-size: 16px;
}

.sectionContent {
  padding: 0 15px;
}

.iframe {
  border: 0;
}
</style>
